// ═══════════════════════════════════════════════════════════════
// OrgChart.tsx – szervezeti diagram (álló / fekvő), szerkeszthető vagy csak megjelenítő
//
// - beosztott egységek („alá”): a szülő alatt (álló) / jobbra (fekvő), összekötő vonallal
// - törzskari egységek („mellé”): a szülő dobozához oldalra kötve, szaggatott vonallal
// - részletesség: csak egységek / vezetőkkel / minden munkatárssal
// - szerkesztés: húzd az egységet egy másikra (alá kerül) vagy a gyökérre (legfelső szint);
//   a dobozon: ◀ ▶ sorrend, „Alá / Mellé” váltás, + alegység, szerkesztés
// ═══════════════════════════════════════════════════════════════
import React, { useState } from 'react';
import type { OrgChartSettings, OrgUnit } from '../../types';
import { childUnits, unitLeaders, unitPositions, positionPersons, unitLoosePersons, descendantIds, type OrgModel } from './orgModel';
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Plus, Edit2, ArrowRightLeft, Crown, GripVertical } from 'lucide-react';

export interface OrgChartEditHandlers {
  onMove: (unitId: number, newParentId: number | null) => void;
  onTogglePlacement: (unit: OrgUnit) => void;
  onReorder: (unit: OrgUnit, dir: -1 | 1) => void;
  onAddChild: (parentId: number | null) => void;
  onEdit: (unit: OrgUnit) => void;
}

interface Props {
  model: OrgModel;
  settings: OrgChartSettings;
  rootTitle: string;
  /** csak ennek az egységnek a részfája (élő beszúrás egy egységre) */
  rootUnitId?: number | null;
  edit?: OrgChartEditHandlers;
  brandColor?: string;
  /** a doboz tartalma a név alatt (alapértelmezés: vezetők / munkatársak) – pl. a kapcsolati térképen a szerv típusa és a kapcsolat */
  renderExtra?: (u: OrgUnit, staff: boolean) => React.ReactNode;
  /** feliratok más felhasználáshoz (kapcsolati térkép) */
  labels?: { addChild?: string; addTop?: string; staffTag?: string; toBeside?: string; toBelow?: string; emptyRoot?: string };
}

export const OrgChart: React.FC<Props> = ({ model, settings, rootTitle, rootUnitId, edit, brandColor = '#4f46e5', renderExtra, labels = {} }) => {
  const L = {
    addChild: 'Alegység felvétele', addTop: 'Legfelső szintű egység felvétele', staffTag: 'törzskari egység',
    toBeside: 'Törzskari egység legyen (a fölérendelt mellé)', toBelow: 'Beosztott egység legyen (alá)', emptyRoot: 'A szervezet vezetése',
    ...labels,
  };
  const [dragId, setDragId] = useState<number | null>(null);
  const [overId, setOverId] = useState<number | 'root' | null>(null);
  const horizontal = settings.orientation === 'horizontal';

  const canDropOn = (target: number | null) => {
    if (dragId === null) return false;
    if (target === null) return true;
    return !descendantIds(model, dragId).has(target);
  };
  const dropProps = (target: number | null) => (edit ? {
    onDragOver: (e: React.DragEvent) => { if (canDropOn(target)) { e.preventDefault(); setOverId(target ?? 'root'); } },
    onDragLeave: () => setOverId((o) => (o === (target ?? 'root') ? null : o)),
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      const id = dragId;
      setDragId(null); setOverId(null);
      if (id !== null && canDropOn(target)) edit.onMove(id, target);
    },
  } : {});

  const peopleLines = (u: OrgUnit, staff = false) => {
    if (renderExtra) return renderExtra(u, staff);
    if (settings.detail === 'units') return null;
    const leaders = unitLeaders(model, u.id);
    const lines: React.ReactNode[] = [];
    for (const l of leaders) {
      if (!l.persons.length) lines.push(<div key={`lp${l.position.id}`} className="oc-person oc-leader"><Crown className="w-3 h-3" /> <span className="opacity-70">{l.position.title}</span></div>);
      for (const p of l.persons) lines.push(
        <div key={`l${l.position.id}-${p.id}`} className="oc-person oc-leader" title={l.position.title}>
          <Crown className="w-3 h-3 shrink-0" /> <span><strong>{p.name}</strong><span className="block text-[10px] opacity-75">{p.title || l.position.title}</span></span>
        </div>,
      );
    }
    if (settings.detail === 'all') {
      const seen = new Set(leaders.flatMap((l) => l.persons.map((p) => p.id)));
      for (const pos of unitPositions(model, u.id).filter((p) => !p.is_leader)) {
        for (const p of positionPersons(model, pos.id)) {
          if (seen.has(p.id)) continue;
          seen.add(p.id);
          lines.push(<div key={`p${pos.id}-${p.id}`} className="oc-person">{p.name}<span className="opacity-70"> – {p.title || pos.title}</span></div>);
        }
      }
      for (const p of unitLoosePersons(model, u.id)) {
        if (seen.has(p.id)) continue;
        seen.add(p.id);
        lines.push(<div key={`x${p.id}`} className="oc-person">{p.name}{p.title ? <span className="opacity-70"> – {p.title}</span> : null}</div>);
      }
    }
    return lines.length ? <div className="oc-people">{lines}</div> : null;
  };

  const unitBox = (u: OrgUnit, staff: boolean) => {
    const isOver = overId === u.id;
    return (
      <div
        className={`oc-box ${staff ? 'oc-staff-box' : ''} ${isOver ? 'oc-over' : ''} ${dragId === u.id ? 'opacity-40' : ''}`}
        style={{ borderTopColor: staff ? undefined : brandColor }}
        draggable={!!edit}
        onDragStart={edit ? (e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(u.id)); setDragId(u.id); } : undefined}
        onDragEnd={edit ? () => { setDragId(null); setOverId(null); } : undefined}
        {...dropProps(u.id)}
      >
        <div className="oc-title">
          {edit && <GripVertical className="w-3 h-3 opacity-40 shrink-0 cursor-grab" aria-hidden="true" />}
          <span>{u.name}</span>
        </div>
        {staff && <div className="oc-tag">{L.staffTag}</div>}
        {peopleLines(u, staff)}
        {/* törzskari egység saját beosztott egységei röviden */}
        {staff && childUnits(model, u.id, 'below').length > 0 && (
          <div className="oc-sub">{childUnits(model, u.id, 'below').map((c) => <div key={c.id}>• {c.name}</div>)}</div>
        )}
        {edit && (
          <div className="oc-tools" onClick={(e) => e.stopPropagation()}>
            <button type="button" title={horizontal ? 'Feljebb' : 'Balra (előrébb a sorrendben)'} aria-label="Előrébb" onClick={() => edit.onReorder(u, -1)}>{horizontal ? <ChevronUp className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}</button>
            <button type="button" title={horizontal ? 'Lejjebb' : 'Jobbra (hátrébb a sorrendben)'} aria-label="Hátrébb" onClick={() => edit.onReorder(u, 1)}>{horizontal ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}</button>
            <button type="button" title={staff ? L.toBelow : L.toBeside} aria-label="Alá / mellé váltás" onClick={() => edit.onTogglePlacement(u)}>
              <ArrowRightLeft className="w-3 h-3" /><span>{staff ? 'Alá' : 'Mellé'}</span>
            </button>
            <button type="button" title={L.addChild} aria-label={L.addChild} onClick={() => edit.onAddChild(u.id)}><Plus className="w-3 h-3" /></button>
            <button type="button" title="Szerkesztés" aria-label="Szerkesztés" onClick={() => edit.onEdit(u)}><Edit2 className="w-3 h-3" /></button>
          </div>
        )}
      </div>
    );
  };

  /** csomópont: a doboz + mellé kötött törzskari egységek (a doboz középre igazítva marad) */
  const nodeWithStaff = (box: React.ReactNode, staffUnits: OrgUnit[]) => {
    if (!staffUnits.length) return box;
    const staff = (hidden: boolean) => (
      <div className={`oc-staff ${hidden ? 'oc-ghost' : ''}`} aria-hidden={hidden || undefined}>
        {staffUnits.map((s) => (
          <div key={s.id} className="oc-staff-item">
            <span className="oc-staff-line" />
            {hidden ? <div className="oc-box oc-staff-box">{s.name}{peopleLines(s, true)}</div> : unitBox(s, true)}
          </div>
        ))}
      </div>
    );
    return <div className="oc-nodewrap">{staff(true)}{box}{staff(false)}</div>;
  };

  const renderUnit = (u: OrgUnit, depth: number): React.ReactNode => {
    const kids = depth > 14 ? [] : childUnits(model, u.id, 'below');
    return (
      <li key={u.id}>
        {nodeWithStaff(unitBox(u, false), childUnits(model, u.id, 'beside'))}
        {kids.length > 0 && <ul>{kids.map((k) => renderUnit(k, depth + 1))}</ul>}
      </li>
    );
  };

  const rootUnit = rootUnitId ? model.units.find((u) => u.id === rootUnitId) : null;
  const top = rootUnit ? [] : childUnits(model, null, 'below');
  const topStaff = rootUnit ? [] : childUnits(model, null, 'beside');

  return (
    <div className="oc-scroll">
      <div className={`oc ${horizontal ? 'oc-h' : 'oc-v'}`} style={{ ['--oc-brand' as any]: brandColor }}>
        <ul>
          {rootUnit ? renderUnit(rootUnit, 0) : (
            <li>
              {nodeWithStaff(
                <div className={`oc-box oc-root ${overId === 'root' ? 'oc-over' : ''}`} style={{ background: brandColor }} {...dropProps(null)}>
                  <div className="oc-title"><span>{rootTitle || L.emptyRoot}</span></div>
                  {edit && (
                    <div className="oc-tools">
                      <button type="button" title={L.addTop} aria-label={L.addTop} onClick={() => edit.onAddChild(null)}><Plus className="w-3 h-3" /></button>
                    </div>
                  )}
                </div>,
                topStaff,
              )}
              {top.length > 0 && <ul>{top.map((u) => renderUnit(u, 1))}</ul>}
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default OrgChart;
